import React, { useState, useRef, useEffect } from 'react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import api from '../services/api';

const CODEBASES = [
  { key: 'express', label: 'Express.js', database: 'sc-demo-express' },
  { key: 'iseries', label: 'iSeries', database: 'sc-demo-iseries' },
];

const SAMPLE_CHIPS = {
  express: [
    'what does application.js import?',
    'which files have most dependencies?',
    'which files are safe to modify?',
    'how many files in this codebase?',
  ],
  iseries: [
    'what does CHKOUT.rpgle call?',
    'which programs open BOOKPF?',
    'show all procedures',
    'how many files in this codebase?',
  ],
};

const STATS = {
  express: { files: 47, types: 'JS, JSON, MD', coverage: '94%' },
  iseries: { files: 32, types: 'RPGLE, DSPF, PF', coverage: '88%' },
};

const SkillCode = () => {
  const [activeCodebase, setActiveCodebase] = useState('express');
  const [activeSubTab, setActiveSubTab] = useState('chat');
  const [chatHistory, setChatHistory] = useState([]);
  const [previousHistory, setPreviousHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const historyEndRef = useRef(null);
  const inputRef = useRef(null);

  const currentCodebase = CODEBASES.find(c => c.key === activeCodebase);
  const stats = STATS[activeCodebase];

  const fetchHistory = async (database, page = 1, append = false) => {
    setHistoryLoading(true);
    try {
      const histRes = await api.get(
        `/skillcode/history?database=${database}&limit=10&page=${page}`
      );
      const conversations = histRes.conversations || [];

      // TODO: history reload doesn't yet handle source='skillbot' answers
      // (messages[1].content is plain text, not JSON). New chats work; old skillbot
      // chats reload as empty. Future work.
      const mapped = (conversations || [])
        .filter(c => c.messages && c.messages.length >= 2)
        .map(c => {
          let results = [];
          try { results = JSON.parse(c.messages[1]?.content || '[]'); } catch { results = []; }
          return {
            question: c.messages[0]?.content,
            results,
            error: c.metadata?.success === false ? c.messages[1]?.content : null,
            timestamp: new Date(c.createdAt).getTime(),
            isHistory: true,
          };
        })
        .reverse(); // oldest first so they read top-to-bottom

      if (append) {
        setPreviousHistory(prev => [...mapped, ...prev]);
      } else {
        setPreviousHistory(mapped);
      }
      setHasMoreHistory(conversations?.length === 10);
      setHistoryPage(page);
    } catch (err) {
      console.error('[SkillCode] History load error:', err.message);
    }
    setHistoryLoading(false);
  };

  // Load history on mount and when codebase changes
  useEffect(() => {
    const db = CODEBASES.find(c => c.key === activeCodebase)?.database;
    if (db) fetchHistory(db, 1);
  }, [activeCodebase]);

  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, loading]);

  const submitQuestion = async (q) => {
    const trimmed = q.trim();
    if (!trimmed || loading) return;

    // Switch to chat tab if on dashboard
    if (activeSubTab !== 'chat') setActiveSubTab('chat');

    setLoading(true);
    setInputValue('');

    const entry = {
      question: trimmed,
      results: null,
      answer: null,
      source: null,
      intent: null,
      cypher: null,
      error: null,
      timestamp: Date.now()
    };

    try {
      const data = await api.post('/skillcode/query', {
        question: trimmed,
        database: currentCodebase.database,
      });
      entry.results = data.results || data.data || (Array.isArray(data) ? data : []);
      entry.answer = data.answer || null;
      entry.source = data.source || null;
      entry.intent = data.intent || null;
      entry.cypher = data.cypher || null;
    } catch (err) {
      entry.error = err.message || 'SkillCode service is starting up, please try again in 30 seconds';
    }

    setChatHistory(prev => [...prev, entry]);
    setLoading(false);
    inputRef.current?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitQuestion(inputValue);
  };

  const handleCodebaseSwitch = (key) => {
    setActiveCodebase(key);
    setChatHistory([]);
    setPreviousHistory([]);
    setHistoryPage(1);
    setHasMoreHistory(false);
    setInputValue('');
  };

  const loadMoreHistory = () => {
    const db = currentCodebase.database;
    fetchHistory(db, historyPage + 1, true);
  };

  const renderTable = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return <p className="text-sm text-gray-500 italic py-2">No results found</p>;
    }

    const keys = Object.keys(data[0]);
    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200 mt-1">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="bg-gray-50">
              {keys.map(key => (
                <th key={key} className="px-4 py-2.5 font-semibold text-gray-700 border-b text-xs uppercase tracking-wide">
                  {key.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {keys.map(key => (
                  <td key={key} className="px-4 py-2.5 text-gray-600 border-b border-gray-100">
                    {typeof row[key] === 'object' && row[key] !== null
                      ? Array.isArray(row[key]) ? row[key].join(', ') : Object.values(row[key]).join(', ')
                      : String(row[key] ?? '-')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Dashboard sub-tab ──
  const renderDashboard = () => (
    <div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Files Indexed</p>
          <p className="text-2xl font-bold" style={{ color: '#7F77DD' }}>{stats.files}</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Source Types</p>
          <p className="text-2xl font-bold" style={{ color: '#7F77DD' }}>{stats.types}</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Coverage</p>
          <p className="text-2xl font-bold" style={{ color: '#7F77DD' }}>{stats.coverage}</p>
        </div>
      </div>

      {/* Sample questions */}
      <div>
        <h3 className="text-sm font-semibold text-gray-600 mb-3">Sample Questions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {SAMPLE_CHIPS[activeCodebase].map((chip, i) => (
            <button
              key={i}
              onClick={() => submitQuestion(chip)}
              className="text-left px-4 py-3 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-all text-sm text-gray-700 hover:border-opacity-100"
              style={{ '--hover-border': '#7F77DD' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#7F77DD'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
            >
              <span style={{ color: '#7F77DD', marginRight: '8px' }}>&#8594;</span>
              {chip}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Chat sub-tab ──
  const renderChat = () => (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Chat history */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {/* Empty state — no history and no current session */}
        {previousHistory.length === 0 && chatHistory.length === 0 && !loading && !historyLoading && (
          <div className="flex flex-col items-center justify-center py-16">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#7F77DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-4 opacity-40">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            <p className="text-gray-400 text-sm mb-4">Ask a question or pick a sample from Dashboard</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg">
              {SAMPLE_CHIPS[activeCodebase].map((chip, i) => (
                <button
                  key={i}
                  onClick={() => submitQuestion(chip)}
                  className="px-3 py-1.5 rounded-full text-xs border transition-colors"
                  style={{ borderColor: '#7F77DD50', color: '#7F77DD' }}
                  onMouseEnter={e => { e.target.style.backgroundColor = '#7F77DD10'; }}
                  onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; }}
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* History loading spinner */}
        {historyLoading && (
          <div className="py-4">
            <LoadingSpinner message="Loading history..." />
          </div>
        )}

        {/* Previous conversations from MongoDB */}
        {previousHistory.length > 0 && (
          <>
            <div className="flex items-center gap-3 py-2">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-xs text-gray-400 font-medium tracking-wide">Previous conversations</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {hasMoreHistory && (
              <div className="flex justify-center">
                <button
                  onClick={loadMoreHistory}
                  disabled={historyLoading}
                  className="px-4 py-1.5 text-xs rounded-full border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {historyLoading ? 'Loading...' : 'Load older conversations'}
                </button>
              </div>
            )}

            {previousHistory.map((entry, i) => (
              <div key={`hist-${i}`} className="space-y-3 opacity-70">
                {/* Timestamp */}
                <p className="text-xs text-gray-400 text-right">
                  {new Date(entry.timestamp).toLocaleString()}
                </p>
                {/* Question bubble */}
                <div className="flex justify-end">
                  <div className="max-w-xl px-4 py-2.5 rounded-2xl rounded-br-md text-sm text-white" style={{ backgroundColor: '#9992DD' }}>
                    {entry.question}
                  </div>
                </div>
                {/* Answer */}
                <div className="bg-gray-50 rounded-2xl rounded-bl-md shadow-sm border border-gray-200 p-4 max-w-3xl">
                  {entry.error ? (
                    <p className="text-sm text-gray-500 italic">{entry.error}</p>
                  ) : entry.source === 'skillbot' && entry.answer ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs text-blue-600">
                        <span className="inline-block px-2 py-0.5 bg-blue-50 rounded-full border border-blue-200">
                          🤖 Answered by SkillBot
                        </span>
                      </div>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {entry.answer}
                      </div>
                    </div>
                  ) : (
                    renderTable(entry.results)
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Session separator — only show when both history and current exist */}
        {previousHistory.length > 0 && (chatHistory.length > 0 || loading) && (
          <div className="flex items-center gap-3 py-2">
            <div className="flex-1 border-t border-gray-300" />
            <span className="text-xs text-gray-500 font-medium tracking-wide">This session</span>
            <div className="flex-1 border-t border-gray-300" />
          </div>
        )}

        {/* Current session Q&A pairs */}
        {chatHistory.map((entry, i) => (
          <div key={i} className="space-y-3">
            {/* Question bubble */}
            <div className="flex justify-end">
              <div className="max-w-xl px-4 py-2.5 rounded-2xl rounded-br-md text-sm text-white" style={{ backgroundColor: '#7F77DD' }}>
                {entry.question}
              </div>
            </div>

            {/* Answer */}
            <div className="bg-white rounded-2xl rounded-bl-md shadow-sm border border-gray-200 p-4 max-w-3xl">
              {entry.error ? (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>{entry.error}</span>
                </div>
              ) : entry.source === 'skillbot' && entry.answer ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-blue-600">
                    <span className="inline-block px-2 py-0.5 bg-blue-50 rounded-full border border-blue-200">
                      🤖 Answered by SkillBot
                    </span>
                  </div>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {entry.answer}
                  </div>
                </div>
              ) : (
                renderTable(entry.results)
              )}
            </div>
          </div>
        ))}

        {/* Loading */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 max-w-3xl">
            <LoadingSpinner message="Querying SkillCode..." />
          </div>
        )}

        <div ref={historyEndRef} />
      </div>

      {/* Bottom input */}
      <div className="px-6 py-4 border-t border-gray-200 bg-white">
        <form onSubmit={handleSubmit} className="flex gap-3">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={`Ask about ${currentCodebase.label}...`}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm"
            style={{ '--tw-ring-color': '#7F77DD' }}
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !inputValue.trim()}
            className="px-6 py-3 text-white rounded-xl font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: '#7F77DD' }}
          >
            Ask
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 'calc(100vh - 20px)' }}>
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-gray-200 bg-white">
        <h1 className="text-xl font-bold text-gray-800">SkillCode</h1>
        <p className="text-sm text-gray-500 mb-4">Software Engineering powered by AI</p>

        {/* Codebase tabs */}
        <div className="flex gap-2 mb-3">
          {CODEBASES.map(cb => (
            <button
              key={cb.key}
              onClick={() => handleCodebaseSwitch(cb.key)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={activeCodebase === cb.key
                ? { backgroundColor: '#7F77DD', color: '#fff' }
                : { backgroundColor: '#f3f4f6', color: '#4b5563' }}
            >
              {cb.label}
            </button>
          ))}
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 border-b border-gray-200 -mb-4">
          {[
            { key: 'dashboard', label: 'Dashboard' },
            { key: 'chat', label: 'Chat' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveSubTab(tab.key)}
              className="px-4 py-2 text-sm font-medium transition-colors relative"
              style={{
                color: activeSubTab === tab.key ? '#7F77DD' : '#6b7280',
                borderBottom: activeSubTab === tab.key ? '2px solid #7F77DD' : '2px solid transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {activeSubTab === 'dashboard' ? renderDashboard() : renderChat()}
    </div>
  );
};

export default SkillCode;
